import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, X, Plus, AlertTriangle, CheckCircle2, HelpCircle, FileText } from "lucide-react";

const STATUS_STYLE = {
  matched: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending_review: "bg-amber-50 text-amber-800 border-amber-200",
  approved_for_payment: "bg-blue-50 text-blue-800 border-blue-200",
  disputed: "bg-red-50 text-red-800 border-red-200",
  paid: "bg-gray-100 text-gray-700 border-gray-300",
};

function money(n) {
  const v = n || 0;
  const sign = v < 0 ? "-" : "";
  return sign + "$" + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function HaulerInvoices() {
  const [invs, setInvs] = useState([]);
  const [haulers, setHaulers] = useState([]);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    const [i, h] = await Promise.all([api.get("/hauler-invoices"), api.get("/haulers")]);
    setInvs(i.data); setHaulers(h.data);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!window.confirm("Delete this hauler invoice?")) return;
    try { await api.delete(`/hauler-invoices/${id}`); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="Hauler Invoices"
        subtitle="Reconciliation & payment"
        actions={
          <button onClick={() => setOpen(true)} data-testid="new-hinv-button" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5">
            + New Invoice
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="hinv-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-2.5 label-caps">HINV #</th>
                <th className="text-left px-4 py-2.5 label-caps">Hauler</th>
                <th className="text-left px-4 py-2.5 label-caps">Invoice #</th>
                <th className="text-right px-4 py-2.5 label-caps">Claimed</th>
                <th className="text-right px-4 py-2.5 label-caps">System</th>
                <th className="text-right px-4 py-2.5 label-caps">Delta</th>
                <th className="text-right px-4 py-2.5 label-caps">Exc.</th>
                <th className="text-left px-4 py-2.5 label-caps">Status</th>
                <th className="text-right px-4 py-2.5 label-caps">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invs.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-12 text-center text-sm text-gray-500">No hauler invoices yet. Enter one from a hauler's PDF/email.</td></tr>
              ) : invs.map((i) => (
                <tr key={i.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`hinv-row-${i.hinv_number}`}>
                  <td className="px-4 py-3">
                    <Link to={`/hauler-invoices/${i.id}`} data-testid={`hinv-link-${i.hinv_number}`} className="font-mono text-xs text-gray-900 hover:text-orange-600">{i.hinv_number}</Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800 truncate max-w-[180px]">{i.hauler_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{i.invoice_number}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm">{money(i.total_claimed)}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-gray-600">{money(i.total_system)}</td>
                  <td className={`px-4 py-3 text-right font-mono text-sm font-bold ${Math.abs(i.total_delta) < 0.01 ? "text-emerald-700" : i.total_delta > 0 ? "text-red-700" : "text-amber-700"}`}>
                    {i.total_delta > 0 ? "+" : ""}{money(i.total_delta).replace("-", "-")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {i.exceptions?.length > 0 ? (
                      <span className="inline-flex items-center gap-1 font-mono text-xs text-red-700"><AlertTriangle className="w-3 h-3" /> {i.exceptions.length}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-mono text-xs text-emerald-700"><CheckCircle2 className="w-3 h-3" /> 0</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${STATUS_STYLE[i.status]}`}>
                      {i.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(i.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-hinv-${i.hinv_number}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && <NewHinvDialog haulers={haulers} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); refresh(); }} />}
    </div>
  );
}

function NewHinvDialog({ haulers, onClose, onSaved }) {
  const [form, setForm] = useState({
    hauler_id: "",
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    period_start: "",
    period_end: "",
    tolerance_pct: 2.0,
    notes: "",
  });
  const [lines, setLines] = useState([{ bol_number: "", weight_tons: 0, rate_per_ton: 0, disposal_fee: 0, claimed_amount: 0 }]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (idx, k, v) => setLines((arr) => arr.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine = () => setLines((arr) => [...arr, { bol_number: "", weight_tons: 0, rate_per_ton: 0, disposal_fee: 0, claimed_amount: 0 }]);
  const removeLine = (idx) => setLines((arr) => arr.filter((_, i) => i !== idx));

  const totalClaimed = useMemo(() => lines.reduce((s, l) => s + (parseFloat(l.claimed_amount) || 0), 0), [lines]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.hauler_id) { toast.error("Select a hauler"); return; }
    if (lines.length === 0 || lines.some((l) => !l.bol_number)) { toast.error("At least one BOL required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        line_items: lines.map((l) => ({
          bol_number: l.bol_number.trim(),
          weight_tons: parseFloat(l.weight_tons) || 0,
          rate_per_ton: parseFloat(l.rate_per_ton) || 0,
          disposal_fee: parseFloat(l.disposal_fee) || 0,
          claimed_amount: parseFloat(l.claimed_amount) || 0,
        })),
      };
      await api.post("/hauler-invoices", payload);
      toast.success("Invoice submitted, reconciliation complete");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-3xl h-full overflow-y-auto border-l border-gray-200 shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="label-caps">New Hauler Invoice</div>
            <div className="font-display font-bold text-2xl tracking-tight">Enter & Reconcile</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <F label="Hauler">
              <select required value={form.hauler_id} onChange={(e) => set("hauler_id", e.target.value)} className="inp" data-testid="hinv-hauler-select">
                <option value="">Select…</option>
                {haulers.map((h) => <option key={h.id} value={h.id}>{h.company_name}</option>)}
              </select>
            </F>
            <F label="Hauler's Invoice #"><input required value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="inp font-mono" data-testid="hinv-invnum-input" /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Invoice Date"><input type="date" required value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} className="inp font-mono" data-testid="hinv-date-input" /></F>
            <F label="Period Start"><input type="date" value={form.period_start} onChange={(e) => set("period_start", e.target.value)} className="inp font-mono" data-testid="hinv-period-start-input" /></F>
            <F label="Period End"><input type="date" value={form.period_end} onChange={(e) => set("period_end", e.target.value)} className="inp font-mono" data-testid="hinv-period-end-input" /></F>
          </div>
          <F label="Tolerance (% of system amount)"><input type="number" step="0.1" value={form.tolerance_pct} onChange={(e) => set("tolerance_pct", parseFloat(e.target.value) || 0)} className="inp" data-testid="hinv-tolerance-input" /></F>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-caps">Line Items (BOLs from hauler's invoice)</div>
              <button type="button" onClick={addLine} data-testid="hinv-add-line" className="text-xs font-semibold text-orange-600 hover:text-orange-700 uppercase tracking-widest flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
            </div>
            <div className="border border-gray-200 divide-y divide-gray-100">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 label-caps">
                <div className="col-span-3">BOL #</div>
                <div className="col-span-2 text-right">Weight (t)</div>
                <div className="col-span-2 text-right">Rate</div>
                <div className="col-span-2 text-right">Disposal</div>
                <div className="col-span-2 text-right">Claimed</div>
                <div className="col-span-1"></div>
              </div>
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                  <input value={l.bol_number} onChange={(e) => setLine(i, "bol_number", e.target.value)} placeholder="BOL-YYYYMM-NNNN" className="col-span-3 inp font-mono text-xs" data-testid={`hinv-line-bol-${i}`} />
                  <input type="number" step="0.1" value={l.weight_tons} onChange={(e) => setLine(i, "weight_tons", e.target.value)} className="col-span-2 inp text-right font-mono text-xs" data-testid={`hinv-line-weight-${i}`} />
                  <input type="number" step="0.01" value={l.rate_per_ton} onChange={(e) => setLine(i, "rate_per_ton", e.target.value)} className="col-span-2 inp text-right font-mono text-xs" data-testid={`hinv-line-rate-${i}`} />
                  <input type="number" step="0.01" value={l.disposal_fee} onChange={(e) => setLine(i, "disposal_fee", e.target.value)} className="col-span-2 inp text-right font-mono text-xs" />
                  <input type="number" step="0.01" value={l.claimed_amount} onChange={(e) => setLine(i, "claimed_amount", e.target.value)} className="col-span-2 inp text-right font-mono text-xs" data-testid={`hinv-line-amount-${i}`} />
                  <button type="button" onClick={() => removeLine(i)} className="col-span-1 justify-self-end p-1 text-gray-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-between items-center font-mono text-sm px-3">
              <span className="text-gray-500">Total claimed</span>
              <span className="font-bold text-gray-900">{money(totalClaimed)}</span>
            </div>
          </div>

          <F label="Notes"><textarea rows="2" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="inp" /></F>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-hinv-button" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-40">
              {busy ? "Reconciling…" : "Submit & Reconcile"}
            </button>
          </div>
        </form>
        <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) { return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>; }
